import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer, ShareNetwork, ArrowLeft, Warning } from "@phosphor-icons/react";
import { getPlanner, getPlannerSummary, listRecipes, getKidsConfig } from "../lib/api";
import { detectAllergens } from "../lib/allergens";

const SLOTS = ["breakfast", "lunch", "dinner", "snack"];
const SLOT_EMOJI = { breakfast: "🌅", lunch: "🥪", dinner: "🍽️", snack: "🍎" };

function pickEmojiForRecipe(recipe, foodEmoji) {
  const emap = foodEmoji || {};
  for (const ing of recipe?.ingredients || []) {
    const low = (ing.name || "").toLowerCase();
    if (emap[low]) return emap[low];
    for (const [k, v] of Object.entries(emap)) {
      if (low.includes(k) || k.includes(low)) return v;
    }
  }
  return "🍽️";
}

export default function KidCard() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const personIdParam = params.get("person");

  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [kidsConfig, setKidsConfig] = useState(null);
  const [selectedPid, setSelectedPid] = useState(personIdParam || null);

  useEffect(() => {
    (async () => {
      try {
        const [p, s, rec, kc] = await Promise.all([
          getPlanner(id),
          getPlannerSummary(id),
          listRecipes(),
          getKidsConfig(),
        ]);
        setPlan(p); setSummary(s); setRecipes(rec.recipes || []); setKidsConfig(kc);
        if (!selectedPid) {
          // pick first kid person if available
          const firstKid = (p.people || []).find(pp => (kc.kid_profile_keys || []).includes(pp.profile));
          setSelectedPid(firstKid?.id || p.people?.[0]?.id || null);
        }
      } catch (e) { toast.error("Could not load plan"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const person = plan?.people?.find(p => p.id === selectedPid);
  const stats = person && summary?.per_person?.[person.id];
  const isKid = stats?.is_kid;
  const ageBand = stats?.age_band;

  const dates = useMemo(() => {
    if (!plan) return [];
    const start = new Date(plan.start_date);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i);
      return d.toISOString().slice(0, 10);
    });
  }, [plan]);

  const recipeById = (rid) => recipes.find(r => r.id === rid);
  const safetyFor = (rid) => (ageBand && summary?.kid_safety?.[ageBand]?.[rid]) || null;

  const doPrint = () => window.print();
  const doShare = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — share with grandparents or sitter!"); }
    catch { toast.error("Could not copy link"); }
  };

  if (!plan) return <div className="p-8 font-mono text-muted-foreground">Loading…</div>;

  const highlightKeys = kidsConfig?.highlight_nutrients || [];
  const highlightEmoji = { protein: "💪", calcium: "🦴", iron: "🩸", vitamin_d: "☀️", fiber: "🌾", zinc: "🛡️", vitamin_c: "🍊", vitamin_a: "🥕" };

  return (
    <div className="mx-auto max-w-[1100px] px-6 lg:px-10 py-8" data-testid="kid-card-page">
      {/* Screen-only toolbar */}
      <div className="print:hidden flex flex-wrap items-center justify-between gap-3 mb-6">
        <Link to={`/planner`} className="h-9 px-3 border border-border rounded-lg hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
          <ArrowLeft size={13} /> Back
        </Link>
        <div className="flex items-center gap-2">
          {plan.people?.length > 1 && (
            <select value={selectedPid || ""} onChange={e => setSelectedPid(e.target.value)}
              data-testid="card-person-select"
              className="h-9 px-2 rounded-lg border border-border bg-background font-mono text-xs">
              {plan.people.map(p => <option key={p.id} value={p.id}>{p.name} · {p.profile}</option>)}
            </select>
          )}
          <button onClick={doShare} data-testid="card-share-btn"
            className="h-9 px-3 border border-pink-500 text-pink-600 hover:bg-pink-500 hover:text-white rounded-lg font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200">
            <ShareNetwork size={13} /> Copy link
          </button>
          <button onClick={doPrint} data-testid="card-print-btn"
            className="h-9 px-4 bg-pink-500 text-white rounded-lg font-mono text-xs uppercase tracking-[0.15em] hover:bg-pink-600 flex items-center gap-1.5 transition-colors duration-200">
            <Printer size={14} /> Print card
          </button>
        </div>
      </div>

      {/* Card body — print-friendly */}
      <div className="bg-white border-2 border-pink-300 rounded-3xl p-8 shadow-sm print:shadow-none print:rounded-none print:border-black">
        {/* Header */}
        <div className="border-b-2 border-dashed border-pink-200 pb-5 mb-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-pink-500">Family Meal Plan · Kids Edition</div>
              <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-pink-700 mt-2">
                {person?.name || "Little Chef"}'s Week 🎨
              </h1>
              <p className="mt-2 text-pink-800/80 font-mono text-sm">
                {stats?.dri?.label} · Week of {plan.start_date}
              </p>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-pink-500">Plan</div>
              <div className="font-display font-bold text-xl">{plan.name}</div>
            </div>
          </div>

          {!isKid && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 print:border-black" data-testid="nonkid-warning">
              Note: {person?.name}'s profile isn't a kid persona — this card still prints, but kid DRIs won't reflect their true target.
            </div>
          )}
        </div>

        {/* Kid-highlight coverage badges */}
        {stats && (
          <div className="mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-pink-500 mb-2">This week's coverage</div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
              {highlightKeys.map(k => {
                const pct = Math.min(999, stats.avg_pcts?.[k] || 0);
                return (
                  <div key={k} className="text-center border border-pink-200 rounded-xl p-2 print:border-black" data-testid={`card-stat-${k}`}>
                    <div className="text-2xl">{highlightEmoji[k]}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-pink-700 mt-1 truncate">{k.replace("_", " ")}</div>
                    <div className="font-display font-bold text-lg text-pink-800">{pct}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Allergy & dislike note */}
        {(person?.allergies?.length || person?.dislikes?.length) ? (
          <div className="mb-6 border-2 border-amber-300 rounded-2xl p-4 bg-amber-50 print:border-black" data-testid="allergy-note">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={16} weight="fill" className="text-amber-600" />
              <span className="font-display font-bold text-base text-amber-800">Allergies & dislikes</span>
            </div>
            <div className="text-sm text-amber-900">
              {(person.allergies?.length ?? 0) > 0 && (
                <div><strong>Avoid (allergy):</strong> {person.allergies.join(", ")}</div>
              )}
              {(person.dislikes?.length ?? 0) > 0 && (
                <div className="mt-1"><strong>Doesn't like:</strong> {person.dislikes.join(", ")}</div>
              )}
            </div>
          </div>
        ) : null}

        {/* Weekly meal grid */}
        <div className="border-2 border-pink-200 rounded-2xl overflow-hidden print:border-black">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-pink-100 print:bg-white">
                <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-pink-700 w-24">Day</th>
                {SLOTS.map(s => (
                  <th key={s} className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-pink-700">
                    <span className="mr-1">{SLOT_EMOJI[s]}</span>{s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map(d => (
                <tr key={d} className="border-t border-pink-200 print:border-black align-top">
                  <td className="px-3 py-3 font-mono text-xs text-pink-700 font-semibold">
                    <div>{new Date(d).toLocaleDateString(undefined, { weekday: "short" })}</div>
                    <div className="text-[10px] text-pink-500">{d.slice(5)}</div>
                  </td>
                  {SLOTS.map(slot => {
                    const meal = plan.meals?.[d]?.[slot];
                    const r = meal && recipeById(meal.recipe_id);
                    const safety = r && safetyFor(r.id);
                    const emoji = r ? pickEmojiForRecipe(r, kidsConfig?.food_emoji) : "";
                    const hasHazard = safety?.badges?.find(b => b.tone === "danger");
                    return (
                      <td key={slot} className="px-3 py-3 border-l border-pink-200 print:border-black" data-testid={`card-cell-${d}-${slot}`}>
                        {r ? (
                          <div>
                            <div className="flex items-start gap-2">
                              <span className="text-2xl leading-none">{emoji}</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium leading-tight">{r.name}</div>
                                <div className="font-mono text-[9px] text-pink-500/80 mt-0.5">{Math.round(r.per_serving?.calories || 0)} kcal/srv</div>
                              </div>
                            </div>
                            {hasHazard && (
                              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-700 font-mono text-[9px] uppercase" data-testid={`card-hazard-${d}-${slot}`}>
                                <Warning size={9} weight="fill" /> Hazard
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-pink-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer for sitter/grandparent */}
        <div className="mt-8 pt-5 border-t-2 border-dashed border-pink-200 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-pink-500 mb-1">Notes for sitter/grandparent</div>
            <div className="h-16 border border-dashed border-pink-300 rounded-lg print:border-black" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-pink-500 mb-1">Emergency contact</div>
            <div className="h-16 border border-dashed border-pink-300 rounded-lg print:border-black" />
          </div>
        </div>

        <div className="mt-6 text-center font-mono text-[10px] text-pink-500/70 uppercase tracking-[0.2em]">
          Generated by NUTRIENT/LAB · {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
